import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX, RefObject } from 'react'
import * as api from '../api/client'
import type { AudioTrack, Line, Role } from '../api/types'
import { describeError } from '../api/errors'
import { HEADPHONES_ACK_KEY } from '../lib/prefs'
import { pushToast } from '../lib/toast'
import { formatTime } from '../lib/format'
import { trackTitle } from '../lib/tracks'
import { Modal } from './Modal'
import { GuideTrackPlayer } from '../audio/guide'
import type { GuideState } from '../audio/guide'
import { isRecordingSupported, pickMimeType } from '../audio/mime'
import {
  LineRecording,
  PREROLL_SECONDS,
  RecordingCancelled,
  TAIL_SECONDS,
  listMicDevices,
  requestMicStream,
  stopStream,
} from '../audio/recorder'
import type { RecordedTake, RecordingPhase } from '../audio/recorder'
import { clearRecorderLag, getRecorderLag, runCalibration, setRecorderLag } from '../audio/calibration'

const GUIDE_ENABLED_KEY = 'ozvuchka.guideEnabled'
const GUIDE_GAIN_KEY = 'ozvuchka.guideGainDb'

export type RecordPanelProps = {
  line: Line | null
  role: Role | undefined
  videoRef: RefObject<HTMLVideoElement | null>
  hasVideo: boolean
  uploadProgress: number | null
  /** Дорожка оригинальных реплик; в рендер не идёт — только в наушники актёра. */
  guideTrack: AudioTrack | null
  onRecorded: (take: RecordedTake) => Promise<void>
  onRecordingChange?: (recording: boolean) => void
}

type Phase = RecordingPhase | 'uploading'

const GUIDE_STATUS_TEXT: Record<GuideState['status'], string> = {
  absent: 'дорожка не выбрана',
  idle: 'ещё не загружена',
  loading: 'декодирую…',
  ready: 'готова',
  error: 'не удалось декодировать',
}

function readGuideGain(): number {
  const raw = Number(localStorage.getItem(GUIDE_GAIN_KEY))
  return Number.isFinite(raw) && raw >= -40 && raw <= 6 ? raw : -6
}

export function RecordPanel({
  line,
  role,
  videoRef,
  hasVideo,
  uploadProgress,
  guideTrack,
  onRecorded,
  onRecordingChange,
}: RecordPanelProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('idle')
  const [countdown, setCountdown] = useState(0)
  const [devices, setDevices] = useState<Array<{ deviceId: string; label: string }>>([])
  const [deviceId, setDeviceId] = useState('')
  const [micReady, setMicReady] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [lag, setLag] = useState(getRecorderLag())
  const [calibrating, setCalibrating] = useState(false)

  const [guideEnabled, setGuideEnabled] = useState(
    () => localStorage.getItem(GUIDE_ENABLED_KEY) === '1',
  )
  const [guideGainDb, setGuideGainDb] = useState(readGuideGain)
  const [guideState, setGuideState] = useState<GuideState>({
    status: 'absent',
    error: null,
    active: false,
  })

  const streamRef = useRef<MediaStream | null>(null)
  const sessionRef = useRef<LineRecording | null>(null)
  const guideRef = useRef<GuideTrackPlayer | null>(null)
  const supported = isRecordingSupported()
  const mime = pickMimeType()

  const releaseMic = useCallback((): void => {
    stopStream(streamRef.current)
    streamRef.current = null
    setMicReady(false)
  }, [])

  useEffect(() => {
    return () => {
      sessionRef.current?.cancel()
      stopStream(streamRef.current)
      streamRef.current = null
      guideRef.current?.dispose()
      guideRef.current = null
    }
  }, [])

  // ------------------------------------------------------ guide-дорожка

  // Громкость через ref: плеер может быть создан позже, чем изменится слайдер.
  const guideGainRef = useRef(guideGainDb)
  guideGainRef.current = guideGainDb

  const ensureGuide = useCallback((): GuideTrackPlayer | null => {
    if (guideRef.current) return guideRef.current
    const video = videoRef.current
    if (!video) return null
    const player = new GuideTrackPlayer(video)
    player.setStateListener(setGuideState)
    player.setGainDb(guideGainRef.current)
    guideRef.current = player
    return player
  }, [videoRef])

  useEffect(() => {
    localStorage.setItem(GUIDE_ENABLED_KEY, guideEnabled ? '1' : '0')
    if (!guideEnabled) {
      const player = guideRef.current
      if (player) {
        void player.setActive(false)
        player.setTrack(null)
      }
      return
    }
    const player = ensureGuide()
    if (!player) return
    player.setTrack(guideTrack ? { id: guideTrack.id, url: api.trackUrl(guideTrack.id) } : null)
    // Декодируем заранее: к моменту записи буфер должен быть готов.
    if (guideTrack) void player.preload()
  }, [guideEnabled, guideTrack, ensureGuide])

  useEffect(() => {
    localStorage.setItem(GUIDE_GAIN_KEY, String(guideGainDb))
    guideRef.current?.setGainDb(guideGainDb)
  }, [guideGainDb])

  const refreshDevices = useCallback(async (): Promise<void> => {
    try {
      setDevices(await listMicDevices())
    } catch {
      // enumerateDevices может быть запрещён — не критично
    }
  }, [])

  const ensureStream = useCallback(async (): Promise<MediaStream> => {
    const existing = streamRef.current
    if (existing && existing.getAudioTracks().some((t) => t.readyState === 'live')) return existing
    stopStream(existing)
    const stream = await requestMicStream(deviceId || undefined)
    streamRef.current = stream
    setMicReady(true)
    void refreshDevices()
    return stream
  }, [deviceId, refreshDevices])

  const connectMic = async (): Promise<void> => {
    try {
      await ensureStream()
      pushToast('success', 'Микрофон подключён')
    } catch (err) {
      pushToast('error', describeError(err))
    }
  }

  const startRecording = async (): Promise<void> => {
    const video = videoRef.current
    if (!line || !video) return
    if (localStorage.getItem(HEADPHONES_ACK_KEY) !== '1') {
      setShowWarning(true)
      return
    }
    let stream: MediaStream
    try {
      stream = await ensureStream()
    } catch (err) {
      pushToast('error', describeError(err))
      return
    }

    // Оригинальная реплика в наушниках: включаем строго на время записи —
    // в дубль она не попадает и в рендер не идёт никогда.
    let guide: GuideTrackPlayer | null = null
    if (guideEnabled && guideTrack) {
      guide = ensureGuide()
      if (guide) {
        // Плеер мог родиться только сейчас (видео появилось позже тумблера).
        guide.setTrack({ id: guideTrack.id, url: api.trackUrl(guideTrack.id) })
        await guide.setActive(true).catch(() => undefined)
        if (!guide.isReady()) {
          pushToast('info', 'Оригинал ещё декодируется — эта попытка пойдёт без него')
        }
      }
    }

    onRecordingChange?.(true)
    const session = new LineRecording({
      video,
      stream,
      lineStart: line.start,
      lineEnd: line.end,
      recorderLag: lag,
      onProgress: (progress) => {
        setPhase(progress.phase)
        setCountdown(progress.countdown)
      },
    })
    sessionRef.current = session
    try {
      const result = await session.run()
      setPhase('uploading')
      await onRecorded(result)
    } catch (err) {
      if (!(err instanceof RecordingCancelled)) pushToast('error', describeError(err))
    } finally {
      sessionRef.current = null
      if (guide) void guide.setActive(false)
      setPhase('idle')
      setCountdown(0)
      onRecordingChange?.(false)
    }
  }

  /** Досрочный стоп: то, что успели записать, сохраняем. */
  const stopRecording = (): void => {
    sessionRef.current?.finish()
  }

  /** Отмена: дубль выбрасываем. */
  const cancelRecording = (): void => {
    sessionRef.current?.cancel()
  }

  const calibrate = async (): Promise<void> => {
    setCalibrating(true)
    try {
      const stream = await ensureStream()
      const result = await runCalibration(stream)
      setRecorderLag(result.lag)
      setLag(result.lag)
      pushToast('success', `Калибровка готова: задержка ${Math.round(result.lag * 1000)} мс`)
    } catch (err) {
      pushToast('error', describeError(err))
    } finally {
      setCalibrating(false)
    }
  }

  const busy = phase !== 'idle'
  const recording = phase === 'preroll' || phase === 'recording' || phase === 'seeking'

  if (!supported) {
    return (
      <section className="panel">
        <header>
          <h2>Запись</h2>
        </header>
        <p className="muted">
          Этот браузер не поддерживает запись звука (MediaRecorder / getUserMedia). Откройте
          приложение в Chrome, Edge, Firefox или Safari по HTTPS либо на localhost.
        </p>
      </section>
    )
  }

  return (
    <section className="panel">
      <header>
        <h2>Запись реплики</h2>
        {micReady ? (
          <span className="badge" style={{ color: 'var(--success)' }}>
            <span className="dot" /> микрофон
          </span>
        ) : null}
      </header>

      {!line ? (
        <p className="muted">Выберите реплику в списке — запись идёт по одной фразе.</p>
      ) : (
        <div className="stack">
          <div className="row wrap">
            <span className="role-swatch" style={{ background: role?.color ?? '#5a6577' }} />
            <strong>{role?.name ?? 'Без роли'}</strong>
            <span className="mono faint">
              {formatTime(line.start)} → {formatTime(line.end)}
            </span>
          </div>
          <div className="muted">{line.text || 'Текст реплики не задан'}</div>
          {line.textOriginal && line.textOriginal.trim() ? (
            <div className="line-original" title="Оригинальная реплика — по ней сверяют интонацию">
              {line.textOriginal}
            </div>
          ) : null}

          <div className={`record-status${recording ? ' active' : ''}`}>
            {phase === 'idle' ? (
              <span className="faint">
                Преролл {PREROLL_SECONDS.toFixed(1)} с, автостоп через {TAIL_SECONDS.toFixed(1)} с
                после конца
              </span>
            ) : null}
            {phase === 'seeking' ? <span className="faint">Отматываю видео…</span> : null}
            {phase === 'preroll' ? (
              <>
                <div className="countdown">{Math.max(0, countdown).toFixed(1)}</div>
                <span className="faint">приготовьтесь</span>
              </>
            ) : null}
            {phase === 'recording' ? (
              <>
                <div className="row">
                  <span className="rec-dot" />
                  <strong>Идёт запись</strong>
                </div>
                <span className="faint">говорите</span>
              </>
            ) : null}
            {phase === 'finishing' ? <span className="faint">Завершаю запись…</span> : null}
            {phase === 'uploading' ? (
              <>
                <strong>Отправляю дубль…</strong>
                <div className="progress" style={{ width: 200 }}>
                  <i style={{ width: `${Math.round((uploadProgress ?? 0) * 100)}%` }} />
                </div>
              </>
            ) : null}
          </div>

          <div className="row wrap">
            {recording ? (
              <>
                <button type="button" className="danger" onClick={stopRecording}>
                  ■ Стоп и сохранить
                </button>
                <button type="button" className="ghost" onClick={cancelRecording}>
                  Отменить
                </button>
              </>
            ) : (
              <button
                type="button"
                className="record"
                disabled={busy || !hasVideo}
                onClick={() => void startRecording()}
                title={hasVideo ? 'Записать реплику' : 'Сначала загрузите видео'}
              >
                ● Записать
              </button>
            )}
            {!micReady ? (
              <button type="button" onClick={() => void connectMic()} disabled={busy}>
                Подключить микрофон
              </button>
            ) : (
              <button type="button" className="ghost" onClick={releaseMic} disabled={busy}>
                Освободить микрофон
              </button>
            )}
          </div>
        </div>
      )}

      <hr />

      <div className="stack">
        <div>
          <label htmlFor="mic-device">Устройство записи</label>
          <select
            id="mic-device"
            value={deviceId}
            disabled={busy}
            onChange={(e) => {
              setDeviceId(e.currentTarget.value)
              releaseMic()
            }}
            onFocus={() => void refreshDevices()}
          >
            <option value="">По умолчанию</option>
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </option>
            ))}
          </select>
        </div>

        <div className="stack guide-block">
          <label className="row" style={{ gap: 6, margin: 0, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={guideEnabled}
              disabled={busy}
              onChange={(e) => setGuideEnabled(e.currentTarget.checked)}
            />
            <span style={{ color: 'var(--text)' }}>Слышать оригинал при записи</span>
          </label>

          {guideEnabled ? (
            guideTrack ? (
              <>
                <div className="row wrap">
                  <span className="faint">
                    {trackTitle(guideTrack)} — {GUIDE_STATUS_TEXT[guideState.status]}
                  </span>
                  {guideState.active ? (
                    <span className="badge" style={{ color: 'var(--success)' }}>
                      <span className="dot" /> в наушниках
                    </span>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="guide-gain">
                    Громкость оригинала: <span className="mono">{guideGainDb} дБ</span>
                  </label>
                  <input
                    id="guide-gain"
                    type="range"
                    min={-40}
                    max={6}
                    step={1}
                    value={guideGainDb}
                    onChange={(e) => setGuideGainDb(Number(e.currentTarget.value))}
                  />
                </div>
                {guideState.status === 'error' ? (
                  <p className="faint">
                    Дорожка не декодировалась{guideState.error ? `: ${guideState.error}` : ''}.
                    Браузер может не понимать её формат — попробуйте загрузить WAV.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="faint">
                Дорожки с оригинальными репликами нет. Назначьте какой-нибудь дорожке тип «Реплики»
                в панели дорожек или загрузите её отдельным файлом.
              </p>
            )
          ) : (
            <p className="faint">
              Актёру заметно проще попадать в тайминг и интонацию, когда оригинальная реплика звучит
              в наушниках. В рендер эта дорожка не идёт никогда.
            </p>
          )}
        </div>

        <div className="row wrap">
          <span className="faint">
            Задержка рекордера: <span className="mono">{Math.round(lag * 1000)} мс</span>
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="ghost small"
            disabled={busy || calibrating}
            onClick={() => void calibrate()}
            title="Снимите наушники: щелчок должен дойти до микрофона по воздуху"
          >
            {calibrating ? 'Калибрую…' : 'Калибровать'}
          </button>
          <button
            type="button"
            className="ghost small"
            disabled={busy || calibrating || lag === 0}
            onClick={() => {
              clearRecorderLag()
              setLag(0)
            }}
          >
            Сбросить
          </button>
        </div>
        <p className="faint">
          Формат записи: <span className="mono">{mime?.mimeType || 'по умолчанию'}</span>. Калибровка
          необязательна — сдвиг дубля можно поправить слайдером на слух.
        </p>
      </div>

      {showWarning ? (
        <Modal
          title="Наденьте наушники"
          onClose={() => setShowWarning(false)}
          footer={
            <>
              <button type="button" className="ghost" onClick={() => setShowWarning(false)}>
                Отмена
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="primary"
                onClick={() => {
                  localStorage.setItem(HEADPHONES_ACK_KEY, '1')
                  setShowWarning(false)
                  void startRecording()
                }}
              >
                Наушники надеты, пишем
              </button>
            </>
          }
        >
          <p>
            Запись идёт вместе с воспроизведением видео. Без наушников оригинальная дорожка попадёт
            в микрофон, и убрать её из дубля уже не получится.
          </p>
          <p className="faint">
            Обработка микрофона (эхоподавление, шумодав, автогромкость) отключена намеренно — это
            запись голоса, а не звонок.
          </p>
          <p className="faint">
            То же касается оригинальной реплики в наушниках: без наушников она уйдёт прямо в дубль.
          </p>
        </Modal>
      ) : null}
    </section>
  )
}
